<template>
    <ViewContainer>
        <div v-if="isExchanging">
            <h1>{{ $i18n('sso.title') }}</h1>
            <p>{{ $i18n('sso.signing_in') }}</p>
            <v-progress-linear indeterminate />
        </div>
        <div v-else>
            <h1>{{ $i18n('sso.failed_title') }}</h1>
            <p>{{ errorMessage }}</p>
            <v-btn
                variant="text"
                :href="loginUrl"
            >
                {{ $i18n('sso.actions.back_to_login') }}
            </v-btn>
        </div>
    </ViewContainer>
</template>

<script>
import ViewContainer from '@/components/ViewContainer.vue';
import { exchangeAdminSsoCode } from '@/http/endpoint/auth-service.js';
import { readJsonBody } from '@/http/clients/fetch.js';
import { createSsoSession } from '@/session/main.js';
import {
    WIDGET_DELIVERY, hasOpener, deliverPair, deliverError,
} from '@/session/widget.js';

const SECTION_CLAIM = 'vf:site';

/**
 * The only refusal codes delivered to the widget.
 *
 * All three are decided after auth-service has verified the signed state. Every other code can be
 * raised *before* that verification — `invalid_grant` is what a forged or tampered state gets — and
 * the destination origin comes out of that same unverified state, so posting those would mean
 * posting somewhere nothing has authenticated. They render in the popup instead.
 *
 * `sso_account_not_linked` and `login_banned` name an identity that authenticated at the customer's
 * identity provider and was then refused by us; the receiver has to tell them apart, because one is
 * resolved by the moderator's Viafoura contact and the other is not resolved by retrying.
 * `sso_temporarily_unavailable` is the one that IS resolved by retrying. auth-service#183 raises it
 * only after verification — from the exchange-time allow-list re-check and `AdminSsoUserCheckStep` —
 * and collapses every earlier outage to `invalid_grant`, so a forged state cannot produce it
 * (Colin Hunt, PR #31).
 */
const DELIVERABLE_REFUSALS = [ 'sso_account_not_linked', 'login_banned', 'sso_temporarily_unavailable' ];
const DELIVERY_CLAIM = 'vf:delivery';
const RETURN_ORIGIN_CLAIM = 'vf:return_origin';

export default {
    components: {
        ViewContainer,
    },
    data() {
        return {
            isExchanging: true,
            errorMessage: '',
            loginUrl: '/login',
            isWidgetDelivery: false,
            returnOrigin: '',
        };
    },
    mounted() {
        this.completeLogin();
    },
    methods: {
        async completeLogin() {
            const { error } = this.$route.query;
            const code = singleQueryValue(this.$route.query.code);
            const state = singleQueryValue(this.$route.query.state);

            const claims = state ? readStateClaims(state) : {};
            const delivery = claims[DELIVERY_CLAIM];
            this.isWidgetDelivery = delivery === WIDGET_DELIVERY;
            this.returnOrigin = claims[RETURN_ORIGIN_CLAIM] || '';

            // A delivery mode this version does not know about is refused rather than quietly
            // treated as a console login. The console path would then establish no session at all,
            // because auth-service withholds the console cookies on any non-console delivery — an
            // admin left with neither a session nor an error. This does not cover a *renamed*
            // claim, which reads as absent and is unknowable here; that is why the claim names are
            // a contract to agree rather than to infer.
            if (delivery !== undefined && !this.isWidgetDelivery) {
                this.fail(this.$i18n('sso.errors.sign_in_failed'));
                return;
            }

            if (this.isWidgetDelivery && !hasOpener()) {
                // Nothing this window does can reach the page that opened it — the customer's page
                // is served with Cross-Origin-Opener-Policy: same-origin. Reported here, because
                // there is no one to report it to, and before the exchange, because minting a
                // session nobody can collect is worse than not minting one.
                this.fail(this.$i18n('sso.errors.opener_unreachable'));
                return;
            }

            // Cognito reports a refusal by redirecting here with `error` instead of `code` — an
            // IdP that declined, or a user who cancelled at the IdP's screen.
            if (error) {
                this.fail(messageForCode('provider_refused', this.$i18n.bind(this)));
                return;
            }
            if (!code || !state) {
                this.fail(messageForCode('invalid_callback', this.$i18n.bind(this)));
                return;
            }

            const sectionUuid = claims[SECTION_CLAIM];
            if (!sectionUuid) {
                this.fail(messageForCode('invalid_callback', this.$i18n.bind(this)));
                return;
            }

            try {
                // The response body carries the tokens. The console path does not read them —
                // auth-service sets VfGAccess/VfGRefresh on this response, because VfGRefresh is
                // HttpOnly and a script cannot write it — but the widget has no cookies to receive
                // and the pair is what it is waiting for.
                const response = await exchangeAdminSsoCode(sectionUuid, code, state);

                if (this.isWidgetDelivery) {
                    await this.deliverToWidget(response);
                    return;
                }
                createSsoSession();
            }
            catch (response) {
                this.refuse(await readErrorCode(response));
            }
        },

        /**
         * Hands the minted pair to the widget that opened this window, and closes.
         *
         * No console session is established on this path: no cookie is written here, and the
         * console navigation `createSsoSession` performs is deliberately not taken. A moderator
         * signing in to moderate an article in place has not asked to be logged into the console,
         * and auth-service withholds the four console cookies on this delivery mode for the same
         * reason.
         *
         * @param {Response} response the resolved exchange response
         * @returns {Promise<void>} resolves once the pair is posted, or the failure is rendered
         */
        async deliverToWidget(response) {
            const pair = await readJsonBody(response) || {};

            if (!pair.access_token || !pair.refresh_token) {
                // The exchange succeeded and the pair did not arrive, so there is nothing to
                // deliver. Reported here rather than posted: a receiver told "no tokens" can only
                // say the same thing again.
                this.fail(this.$i18n('sso.errors.sign_in_failed'));
                return;
            }

            if (!deliverPair(this.returnOrigin, pair)) {
                this.fail(this.$i18n('sso.errors.opener_unreachable'));
            }
        },

        /**
         * Reports a refusal the exchange returned.
         *
         * On the widget path that goes to the page that opened this window, which gets the code so
         * it can tell apart the refusals that ask different things of the moderator — an account
         * not linked on this property is resolved by their Viafoura contact, a suspended one is not
         * resolved by trying again, and a temporary outage is. On the console path, and whenever
         * the message cannot be delivered, it is rendered here as before.
         *
         * Only the codes in `DELIVERABLE_REFUSALS` are posted, and that is the point: they are the
         * ones auth-service only reaches after verifying the signed state. A rejected exchange is
         * not by itself proof of verification — `invalid_grant` is exactly what a forged state
         * gets — and the destination is read from that same state, so anything else would be a
         * message sent to an origin nothing has authenticated. Those render here instead.
         *
         * Copilot raised both halves of this on the review: first that pre-exchange refusals were
         * posted, then that a rejected exchange does not imply the state was accepted.
         *
         * @param {string|null} code the refusal code, or null when none was readable
         */
        refuse(code) {
            const deliverable = this.isWidgetDelivery && DELIVERABLE_REFUSALS.includes(code);
            if (deliverable && deliverError(this.returnOrigin, code)) {
                return;
            }
            this.fail(messageForCode(code, this.$i18n.bind(this)));
        },

        fail(message) {
            this.errorMessage = message;
            this.isExchanging = false;
        },
    },
};

/**
 * The single string value of a query parameter, or null if it is absent or repeated.
 *
 * Vue Router hands back a `string[]` when a parameter appears more than once (`?code=a&code=b`).
 * An array reaching the exchange is coerced to a comma-joined string, so `readSectionFromState`
 * gets something that is not a JWT and the admin is told the callback is invalid — technically
 * true, but for a reason nothing in the logs explains.
 *
 * A repeat is rejected rather than resolved by taking the first or last value. A genuine Cognito
 * callback never repeats these, so a repeat means the URL was assembled by something else, and
 * picking one of the values would resolve an ambiguity that someone else introduced.
 *
 * @param {string|string[]|null|undefined} value the raw `$route.query` entry
 * @returns {string|null} the value when it is a single non-empty string, otherwise null
 */
function singleQueryValue(value) {
    return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Reads the claims out of the signed `state`, without verifying it.
 *
 * The callback URL is a single fixed URL — Cognito matches redirect_uri exactly, and one app client
 * per environment serves every customer — so the section cannot travel in the path or the query.
 * It travels inside `state`, and the SPA needs it to address the exchange.
 *
 * Reading an unverified claim is safe *here* precisely because it is not trusted: auth-service
 * verifies the state's signature and then cross-checks the section it contains against the section
 * in the request path. Tampering with this value only produces a mismatch and a refused login.
 *
 * The same reasoning covers the delivery mode and the return origin (CM-1918), with one addition
 * worth stating because a delivery destination is a different kind of value: an origin read here is
 * only ever used *after* the exchange has succeeded against this same state string, and the
 * exchange verifies the signature over these claims. So a forged state cannot nominate a
 * destination — it gets a refused exchange, and no pair to deliver anywhere. auth-service validated
 * the origin against the site's registered redirect hosts before signing it; nothing in the browser
 * could do that check, because the browser is what the check exists to constrain.
 *
 * @param {string} state the raw state parameter
 * @returns {Object} the decoded claims, or an empty object when the state is not readable
 */
function readStateClaims(state) {
    try {
        const payload = state.split('.')[1];
        // base64url -> base64, and restore the padding a JWT omits; atob rejects an unpadded string.
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return JSON.parse(atob(padded)) || {};
    }
    catch (error) {
        return {};
    }
}

/**
 * Maps a refused exchange to something the admin can act on. auth-service is deliberately opaque
 * about *which* check refused an authentication, with two exceptions it reports distinctly: an
 * identity that authenticated at the IdP but has no linked Viafoura account, and a dependency of
 * the login being unreachable. Neither discloses anything to someone who has just proven that
 * identity, and each asks something different of them — go to your Viafoura contact, or wait.
 *
 * @param {string|null} error the refusal code
 * @param {Function} t the translation function
 * @returns {string} the message to show
 */
function messageForCode(error, t) {
    if (error === 'provider_refused') {
        return t('sso.errors.provider_refused');
    }
    if (error === 'invalid_callback') {
        return t('sso.errors.invalid_callback');
    }
    if (error === 'sso_account_not_linked') {
        return t('sso.errors.account_not_linked');
    }
    // Deliberately distinct from account_not_linked, which it used to arrive as. The two need
    // opposite things from the admin: one is resolved by their Viafoura contact and the other by
    // waiting, so telling them apart is the whole reason auth-service separated the codes.
    if (error === 'sso_temporarily_unavailable') {
        return t('sso.errors.temporarily_unavailable');
    }
    if (error === 'login_banned') {
        return t('sso.errors.account_suspended');
    }
    if (error === 'invalid_grant') {
        return t('sso.errors.sign_in_failed');
    }
    if (error === 'not_found') {
        return t('sso.errors.sso_unavailable');
    }
    return t('errors.unexpected');
}

async function readErrorCode(response) {
    try {
        const body = await response.json();
        return body.error;
    }
    catch (error) {
        // A network failure rejects with something that is not a Response at all.
        return null;
    }
}
</script>
